import { forwardRef, useImperativeHandle } from 'react';
import cn from 'classnames';
import {
  type AnimationPlaybackControlsWithThen,
  type DOMKeyframesDefinition,
  motion,
  useAnimate,
} from 'framer-motion';

import { playSound } from '@/common/lib/soundEffect';
import { type ActiveTileRef } from '@/common/types/dice-game';

import css from './styles.module.scss';

interface ActiveTileProps {
  size: number;
  initial?: { x: number; y: number };
}

const ActiveTile = forwardRef<ActiveTileRef, ActiveTileProps>(
  ({ initial = { x: 0, y: 0 }, size }, ref) => {
    const [scope, animate] = useAnimate<HTMLDivElement>();

    // useImperativeHandle(ref, () => ({
    //   animate: async (
    //     keyframes,
    //     options,
    //   ): Promise<AnimationPlaybackControlsWithThen> =>
    //     await animate(scope.current, keyframes, options),
    // }));
    useImperativeHandle(ref, () => ({
      animate: async (
        keyframes,
        options,
      ): Promise<AnimationPlaybackControlsWithThen> => {
        const xIsArray = Array.isArray(keyframes.x);
        const yIsArray = Array.isArray(keyframes.y);

        if (xIsArray || yIsArray) {
          const length = xIsArray
            ? (keyframes.x as number[]).length
            : (keyframes.y as number[]).length;

          const totalDuration = options?.duration ?? 1;
          const stepDuration =
            length > 1 ? totalDuration / (length - 1) : totalDuration;

          // skip animation requested — snap directly to the final keyframe
          if (totalDuration === 0) {
            const finalKeyframes: DOMKeyframesDefinition = {};

            if (xIsArray) {
              finalKeyframes.x = (keyframes.x as number[])[length - 1];
            }

            if (yIsArray) {
              finalKeyframes.y = (keyframes.y as number[])[length - 1];
            }

            return await animate(scope.current, finalKeyframes, {
              ...options,
              duration: 0,
            });
          }

          let lastAnimationResult:
            | AnimationPlaybackControlsWithThen
            | undefined;

          for (let i = 1; i < length; i++) {
            playSound('roll/move');

            const stepKeyframes: DOMKeyframesDefinition = {};

            if (xIsArray) stepKeyframes.x = (keyframes.x as number[])[i];

            if (yIsArray) stepKeyframes.y = (keyframes.y as number[])[i];

            lastAnimationResult = await animate(scope.current, stepKeyframes, {
              ...options,
              duration: stepDuration,
            });
          }

          return lastAnimationResult as unknown as AnimationPlaybackControlsWithThen;
        }

        const isMovement =
          keyframes &&
          ('x' in keyframes ||
            'y' in keyframes ||
            'left' in keyframes ||
            'top' in keyframes);

        if (isMovement) {
          playSound('roll/move');
        }

        return await animate(scope.current, keyframes, options);
      },
    }));

    return (
      <motion.div
        style={{
          width: size,
          height: size,
        }}
        ref={scope}
        initial={initial}
        className={cn(
          'absolute z-[3] left-0 top-0 rounded-lg',
          css.backfaceVisible,
        )}
      >
        <div className={css['tile--active']}></div>
        <span
          className={cn(css.borderGradient, css['borderGradient--first'])}
        ></span>
        <span
          className={cn(css.borderGradient, css['borderGradient--second'])}
        ></span>
      </motion.div>
    );
  },
);

export { ActiveTile };
